<template>
  <div class="v-tag" :class="classes" :style="{ backgroundColor }">
    <slot />

    <VIcon v-if="closable" class="v-tag__close" icon="Close" :size="14" @click.stop="$emit('close')" @mouseup.stop />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { VTagSize, VTagType } from '../types.ts'
import VIcon from './Icon/VIcon.vue'

interface Props {
  type?: VTagType
  plain?: boolean
  backgroundColor?: string
  size?: VTagSize
  closable?: boolean
  rounded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: '',
  plain: true,
  backgroundColor: undefined,
  size: 'medium',
  closable: false,
})

defineEmits<{
  close: []
}>()

defineSlots<{
  default?(): any
}>()

const classes = computed(() => ({
  'v-tag': true,
  [`v-tag--${props.size}`]: true,
  [`v-tag--${props.type}`]: props.type,
  'v-tag--plain': props.plain,
  'v-tag--rounded': props.rounded,
}))
</script>

<style>
.v-tag {
  background-color: var(--color-grey-40);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  font-size: 14px;
  line-height: 1;
  color: #fff;
  border: 1px solid var(--color-grey-40);
  border-radius: var(--border-radius);

  &.is-focused {
    box-shadow: 0 0 0 1px rgb(0 0 0 / 20%);
  }

  &:not(:last-child) {
    margin-right: 5px;
  }
}

.v-tag--rounded {
  border-radius: 52px;
}

.v-tag__close {
  margin-left: 5px;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  transition: background-color var(--transition);

  &:hover {
    background-color: #fff;

    * {
      stroke: var(--color-grey-40);
    }
  }

  * {
    transition: stroke var(--transition);
  }
}

.v-tag--primary {
  background-color: var(--color-primary);
  border-color: var(--color-primary);

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-primary);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

.v-tag--secondary {
  background-color: var(--color-secondary);
  border-color: var(--color-secondary);
  color: #fff;

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-secondary);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

.v-tag--blue-3 {
  background-color: var(--color-blue-3);
  border-color: var(--color-blue-3);
  color: #000;

  .v-tag__close {
    &:hover {
      background-color: #000;

      * {
        stroke: var(--color-blue-3);
      }
    }

    * {
      stroke: #000;
    }
  }
}

.v-tag--blue-5 {
  background-color: var(--color-blue-5);
  border-color: var(--color-blue-5);

  .v-tag__close {
    &:hover {
      background-color: var(--color-primary);

      * {
        stroke: #fff;
      }
    }

    * {
      stroke: var(--color-primary);
    }
  }
}

.v-tag--info {
  background-color: var(--color-grey-6);
  border-color: var(--color-grey-6);
  color: var(--color-grey-60);

  .v-tag__close {
    &:hover {
      background-color: var(--color-grey-60);

      * {
        stroke: var(--color-grey-6);
      }
    }

    * {
      stroke: var(--color-grey-60);
    }
  }
}

.v-tag--green {
  background-color: var(--color-green-light);
  border-color: var(--color-green-light);

  .v-tag__close {
    &:hover {
      background-color: var(--color-green-dark);

      * {
        stroke: var(--color-green-light);
      }
    }

    * {
      stroke: var(--color-green-dark);
    }
  }
}

.v-tag--green-dark {
  background-color: var(--color-green-dark);
  border-color: var(--color-green-dark);

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-green-dark);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

.v-tag--orange {
  background-color: var(--color-orange);
  border-color: var(--color-orange);

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-orange);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

.v-tag--red {
  background-color: var(--color-red);
  border-color: var(--color-red);

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-red);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

.v-tag--red-dark {
  background-color: var(--color-red-dark);
  border-color: var(--color-red-dark);

  .v-tag__close {
    &:hover {
      background-color: #fff;

      * {
        stroke: var(--color-red-dark);
      }
    }

    * {
      stroke: #fff;
    }
  }
}

/** Plain */
.v-tag--plain {
  background-color: transparent;
  color: #4a4a4a;
  border: var(--border);
  border-color: var(--color-grey-40);

  .v-tag__close {
    &:hover {
      background-color: #333;

      * {
        stroke: #fff;
      }
    }

    * {
      stroke: #333;
    }
  }

  &.v-tag--primary {
    color: var(--color-primary);
    border-color: var(--color-primary);

    .v-tag__close {
      &:hover {
        background-color: var(--color-primary);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-primary);
      }
    }
  }

  &.v-tag--secondary {
    color: var(--color-secondary);
    border-color: var(--color-secondary);

    .v-tag__close {
      &:hover {
        background-color: var(--color-secondary);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-secondary);
      }
    }
  }

  &.v-tag--blue-3 {
    color: var(--color-blue-3);
    border-color: var(--color-blue-3);

    .v-tag__close {
      &:hover {
        background-color: var(--color-blue-3);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-blue-3);
      }
    }
  }

  &.v-tag--blue-5 {
    color: var(--color-blue-5);
    border-color: var(--color-blue-5);

    .v-tag__close {
      &:hover {
        background-color: var(--color-blue-5);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-blue-5);
      }
    }
  }

  &.v-tag--info {
    border-color: var(--color-grey-40);

    .v-tag__close {
      &:hover {
        background-color: #333;

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: #333;
      }
    }
  }

  &.v-tag--green {
    color: var(--color-green);
    border-color: var(--color-green);

    .v-tag__close {
      &:hover {
        background-color: var(--color-green);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-green);
      }
    }
  }

  &.v-tag--green-dark {
    color: var(--color-green-dark);
    border-color: var(--color-green-dark);

    .v-tag__close {
      &:hover {
        background-color: var(--color-green-dark);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-green-dark);
      }
    }
  }

  &.v-tag--orange {
    color: var(--color-orange);
    border-color: var(--color-orange);

    .v-tag__close {
      &:hover {
        background-color: var(--color-orange);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-orange);
      }
    }
  }

  &.v-tag--red {
    color: var(--color-red);
    border-color: var(--color-red);

    .v-tag__close {
      &:hover {
        background-color: var(--color-red);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-red);
      }
    }
  }

  &.v-tag--red-dark {
    color: var(--color-red-dark);
    border-color: var(--color-red-dark);

    .v-tag__close {
      &:hover {
        background-color: var(--color-red-dark);

        * {
          stroke: #fff;
        }
      }

      * {
        stroke: var(--color-red-dark);
      }
    }
  }
}

.v-tag--mini {
  padding: 3px 6px;
  font-size: 11px;
  line-height: 9px;

  &.v-tag--rounded {
    min-width: 17px;
  }
}

.v-tag--small {
  font-size: 12px;
  padding: 4px 6px;

  &.v-tag--rounded {
    min-width: 22px;
  }
}

.v-tag--medium {
  padding: 5px 8px;

  &.v-tag--rounded {
    min-width: 26px;
  }
}

.v-tag--large {
  padding: 8px 12px;

  &.v-tag--rounded {
    min-width: 32px;
  }
}
</style>
